import { api } from "./api-client";
import type { ClassVideo } from "@/types/class-video";
import type { ClassVideoSummary } from "@/types/class-video-summary";

export interface ClassVideoInfo {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  profile_id: string;
  instructor_name: string | null;
  instructor_username: string | null;
  instructor_photo: string | null;
  videos_published: boolean;
  videos_notification_sent: boolean;
  song_url: string | null;
  external_video_url: string | null;
}

export async function getClassVideos(
  classId: string
): Promise<{
  class: ClassVideoInfo;
  videos: ClassVideo[];
}> {
  return api.get(`/classes/${classId}/videos`);
}

export async function createClassVideo(
  classId: string,
  input: {
    id: string;
    file_name: string;
    file_size: number;
    file_type: string;
    storage_key: string;
    public_url: string;
    display_name: string;
    display_order: number;
    recorded_at?: string;
  }
): Promise<ClassVideo> {
  const response = await api.post<{ video: ClassVideo }>(
    `/classes/${classId}/videos`,
    input
  );
  return response.video;
}

export async function updateClassVideo(
  classId: string,
  videoId: string,
  input: {
    display_name?: string;
    display_order?: number;
    duration_seconds?: number;
    thumbnail_url?: string;
    thumbnail_urls?: string[];
  }
): Promise<ClassVideo> {
  const response = await api.patch<{ video: ClassVideo }>(
    `/classes/${classId}/videos/${videoId}`,
    input
  );
  return response.video;
}

export async function reorderClassVideos(
  classId: string,
  order: Array<{ id: string; display_order: number }>
): Promise<void> {
  await api.patch(
    `/classes/${classId}/videos/reorder`,
    { order }
  );
}

export async function deleteClassVideo(
  classId: string,
  videoId: string
): Promise<void> {
  await api.delete(
    `/classes/${classId}/videos/${videoId}`
  );
}

export async function getPublicClassVideos(
  classId: string
): Promise<{
  class: ClassVideoInfo;
  videos: ClassVideo[];
}> {
  return api.get(`/classes/${classId}/videos`, { skipAuth: true });
}

export async function publishClassVideos(
  classId: string,
  published: boolean
): Promise<{
  success: boolean;
  videos_published: boolean;
  videos_notification_sent: boolean;
  emails_sent: number;
}> {
  return api.patch(`/classes/${classId}/videos/publish`, { published });
}

// ── Class Videos Summary API ──────────────────────────────────────

export async function getClassVideosSummary(
  profileId: string
): Promise<ClassVideoSummary[]> {
  const response = await api.get<{ classes: ClassVideoSummary[] }>(
    `/profiles/${profileId}/class-videos-summary`
  );
  return response.classes;
}

// ── Music Sync API ─────────────────────────────────────────────────

export async function registerSong(
  classId: string,
  input: { songUrl: string; storageKey: string }
): Promise<{ songUrl: string }> {
  return api.put(`/classes/${classId}/videos/song`, input);
}

export async function deleteSong(classId: string): Promise<void> {
  await api.delete(`/classes/${classId}/videos/song`);
}

export async function detectAudioSync(
  classId: string,
  videoId: string
): Promise<
  | { status: "success"; offsetMs: number; confidence: number }
  | { status: "no_match"; error: string }
  | { status: "error"; error: string }
> {
  return api.post(`/classes/${classId}/videos/${videoId}/sync/detect`);
}

export async function renderAudioSync(
  classId: string,
  videoId: string,
  input: { offsetMs: number; videoVolume: number; songVolume: number }
): Promise<
  | { status: "success"; previewUrl: string }
  | { status: "error"; error: string }
> {
  return api.post(`/classes/${classId}/videos/${videoId}/sync/render`, input);
}

export async function confirmAudioSync(
  classId: string,
  videoId: string
): Promise<{ success: boolean; video: ClassVideo }> {
  return api.post(`/classes/${classId}/videos/${videoId}/sync/confirm`);
}

export async function cleanupSyncPreviews(
  classId: string
): Promise<void> {
  await api.post(`/classes/${classId}/videos/sync/cleanup`);
}
